import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, MapPin, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';

import { API } from '../config';

const INTERESTS = ['Coffee', 'Food tour', 'Shopping', 'Lounge', 'Sightseeing', 'Quiet work', 'Conversation', 'Business', 'Family travel', 'Solo travel'];

export default function BuddyMatcher() {
  const { user } = useAuth();
  const [airports, setAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState('DXB');
  const [layoverDuration, setLayoverDuration] = useState(240);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/airports`).then(r => setAirports(r.data)).catch(() => {});
  }, []);

  const toggleInterest = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const findBuddies = async () => {
    if (!user) { toast.error('Please log in to find buddies'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/buddy/register`, {
        airport_code: selectedAirport,
        layover_duration: layoverDuration,
        interests: selectedInterests,
      }, { withCredentials: true });
      setMatches(res.data.matches || []);
      toast.success(res.data.matches?.length > 0 ? `Found ${res.data.matches.length} potential buddies!` : 'Registered! Check back as more travelers join.');
    } catch {
      toast.error('Failed to search for buddies');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (mins) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  return (
    <PageLayout>
      <SEOHead title="Layover Buddy Matcher" description="Find travel companions during airport layovers. Match with fellow travelers by interests, share your wait time, and turn a boring layover into a great meet." url="/buddy" keywords="layover buddy, airport companion, travel meetup, layover friend finder" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#FF4500]/10 flex items-center justify-center">
            <Users className="w-8 h-8 text-[#FF4500]" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Layover Buddy Matcher</h1>
          <p className="text-muted-foreground">Find fellow travelers with the same layover airport and duration</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Search form */}
          <Card className="fly-card p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Your Layover Details</h2>

            <div className="space-y-5">
              <div>
                <Label className="mb-2 block">Layover Airport</Label>
                <Select value={selectedAirport} onValueChange={setSelectedAirport}>
                  <SelectTrigger data-testid="airport-select">
                    <SelectValue placeholder="Select airport" />
                  </SelectTrigger>
                  <SelectContent>
                    {airports.map(a => (
                      <SelectItem key={a.iata_code} value={a.iata_code}>
                        {a.iata_code} — {a.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Layover Duration: <span className="text-[#FF4500] font-semibold">{formatDuration(layoverDuration)}</span></Label>
                <Slider
                  min={30}
                  max={720}
                  step={30}
                  value={[layoverDuration]}
                  onValueChange={([v]) => setLayoverDuration(v)}
                  className="mt-2"
                  data-testid="layover-duration-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>30min</span><span>12h</span>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Your Interests</Label>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map(interest => (
                    <button
                      key={interest}
                      onClick={() => toggleInterest(interest)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        selectedInterests.includes(interest)
                          ? 'bg-[#FF4500]/15 text-[#FF4500] border border-[#FF4500]/30'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                className="fly-btn-primary w-full py-5"
                onClick={findBuddies}
                disabled={loading}
                data-testid="find-buddies-btn"
              >
                <Users className="mr-2 w-4 h-4" />
                {loading ? 'Searching...' : 'Find My Layover Buddies'}
              </Button>
            </div>
          </Card>

          {/* Matches */}
          <div>
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              {matches.length > 0 ? `${matches.length} Potential Buddies` : 'Matches will appear here'}
            </h2>

            {matches.length === 0 ? (
              <Card className="fly-card p-8 text-center">
                <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Enter your layover details and click search to find traveling companions.</p>
                <p className="text-xs text-muted-foreground mt-2">Your profile will be added to the pool so others can find you too.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {matches.map((match, i) => (
                  <motion.div key={match.buddy_id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}>
                    <Card className="fly-card p-4" data-testid="layover-buddy-card">
                      <div className="flex gap-3">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={match.user_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${match.user_id}`} />
                          <AvatarFallback className="bg-[#1E3A8A] text-white">{match.user_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{match.user_name}</span>
                            <Badge className="text-[10px] status-on-time">Available</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{match.airport_code}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(match.layover_duration)}</span>
                          </div>
                          {match.interests?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {match.interests.slice(0, 3).map(item => (
                                <span key={item} className="text-[10px] px-2 py-0.5 rounded-full bg-muted">{item}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
